import React, { ReactNode, useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Divider,
  Fade,
  Modal as MuiModal,
  Paper,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Button from "./Button";
import { Close } from "@mui/icons-material";
import theme from "../../theme";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  otherFunction?: { label: string; function: () => void };
  children: ReactNode;
  title: string;
  loading?: boolean;
}

const Modal: React.FC<Props> = (props) => {
  const { open, onClose, onSave, otherFunction, children, title, loading } =
    props;

  const handleSaveClick = () => {
    onSave();
  };

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowTopShadow(false);
      setShowBottomShadow(false);
      return;
    }

    const node = contentRef.current;
    if (!node) {
      return;
    }

    const updateShadows = () => {
      const { scrollTop, scrollHeight, clientHeight } = node;
      setShowTopShadow(scrollTop > 0);
      setShowBottomShadow(scrollTop + clientHeight < scrollHeight - 1);
    };

    const raf = requestAnimationFrame(updateShadows);

    node.addEventListener("scroll", updateShadows);
    window.addEventListener("resize", updateShadows);

    return () => {
      cancelAnimationFrame(raf);
      node.removeEventListener("scroll", updateShadows);
      window.removeEventListener("resize", updateShadows);
    };
  }, [open, children]);

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      disableAutoFocus={true}
      closeAfterTransition={true}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Fade in={open}>
        <Paper
          sx={{
            width: { lg: 1024, md: 900, xs: window.innerWidth },
            height: { md: "auto", xs: window.innerHeight },
            maxHeight: "calc(100vh - 64px)",
            overflow: "hidden",
            paddingY: 3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Container
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              flex={1}
              sx={{ minHeight: 0, width: "100%" }}
            >
              <Box
                display="flex"
                flexDirection="row"
                width="100%"
                justifyContent="space-between"
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                >
                  <Typography variant="h4" fontWeight="700">
                    {title}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                >
                  <Button
                    variant={"contained"}
                    color={"inherit"}
                    onClick={onClose}
                    icon={<Close />}
                  />
                </Box>
              </Box>
              <Divider />
              <Box
                ref={contentRef}
                sx={{
                  position: "relative",
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  width: "100%",
                  pr: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    py: 1,
                  }}
                >
                  {children}
                </Box>
                <Box
                  sx={(muiTheme) => ({
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 16,
                    pointerEvents: "none",
                    background: `linear-gradient(${muiTheme.palette.background.paper}, ${alpha(
                      muiTheme.palette.background.paper,
                      0
                    )})`,
                    opacity: showTopShadow ? 1 : 0,
                    transition: "opacity 150ms ease",
                  })}
                />
                <Box
                  sx={(muiTheme) => ({
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 16,
                    pointerEvents: "none",
                    background: `linear-gradient(${alpha(
                      muiTheme.palette.background.paper,
                      0
                    )}, ${muiTheme.palette.background.paper})`,
                    opacity: showBottomShadow ? 1 : 0,
                    transition: "opacity 150ms ease",
                  })}
                />
              </Box>
              {!!onSave && (
                <>
                  <Divider />

                  <Box
                    display="flex"
                    flexDirection="row"
                    width="100%"
                    justifyContent="flex-end"
                    gap={2}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      color={theme.palette.text.secondary}
                    >
                      <Button label="Cancel" cancel onClick={onClose} />
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                    >
                      <Button
                        label="Save"
                        variant={"contained"}
                        color={"primary"}
                        onClick={handleSaveClick}
                        disabled={loading}
                      />
                    </Box>
                    {!!otherFunction && (
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                      >
                        <Button
                          label={otherFunction.label}
                          variant="contained"
                          color="success"
                          onClick={otherFunction.function}
                        />
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Container>
        </Paper>
      </Fade>
    </MuiModal>
  );
};

export default Modal;
